import React from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import { ContactGroup } from './contacts'
import { TextInput } from '../../components/form/textInput'
import { Button, ButtonShape } from '../../components/simple/button'
import { IconShare, IconFileUpload } from '../../icons/library'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { StudentGroupRecipient, StudentRecipient, TeacherRecipient } from './compose/composeTypes'
import { BasicUser } from '../../components/userSelector/users'
import { UserSelector } from '../../components/userSelector/userSelector'
import { loadBasicUsersByIdentifiers } from '../../components/userSelector/userSelectorPlugins'
import { Popups } from '../../providers/popups'
import { inputSingleFile } from '../../components/media/fileUpload'
import { Toasts } from '../../providers/toast'
import { User } from '../../providers/context'

export type EditGroupState = {
  group: ContactGroup | undefined
}

export const NEW_GROUP_STATE: EditGroupState = {
  group: undefined,
}

interface EditGroupModalProps {
  editGroupState: EditGroupState
  setEditGroupState: React.Dispatch<React.SetStateAction<EditGroupState | undefined>>
  setPreloadedRecipients: React.Dispatch<React.SetStateAction<(TeacherRecipient | StudentRecipient)[] | undefined>>
  onSaved: () => void
  user: User
}

export const EditGroupModal: React.FC<EditGroupModalProps> = ({
  editGroupState,
  setEditGroupState,
  setPreloadedRecipients,
  onSaved,
  user,
}) => {
  const [saving, setSaving] = React.useState(false)
  const [name, setName] = React.useState(editGroupState.group?.name || '')
  const [students, setStudents] = React.useState<BasicUser[]>(editGroupState.group?.students || [])
  const [teachers, setTeachers] = React.useState<BasicUser[]>(editGroupState.group?.sharedWithTeachers || [])
  const [uploading, setUploading] = React.useState(false)

  const recipientSearchData = React.useMemo(() => {
    return editGroupState.group ? { group: editGroupState.group.id } : { group: 'new' }
  }, [editGroupState.group])

  const canSave =
    name.trim().length > 0 &&
    // For imported/non-custom groups, we don't require students in the UI since they're managed elsewhere
    ((editGroupState.group && !editGroupState.group.isCustomGroup) ||
      // For custom groups (new or existing), require at least 2 students
      students.length >= 2)

  const save = () => {
    setSaving(true)
    const req = editGroupState.group
      ? makeRequest<StudentGroupRecipient | 'deleted'>('/frontend_api/editGroup', {
          name,
          // For imported/non-custom groups, don't override students/teachers if they're not shown in UI
          ...(editGroupState.group.isCustomGroup
            ? {
                students: students.map((student) => student.id),
                teachers: teachers.map((teacher) => teacher.id),
              }
            : {}),
          id: editGroupState.group!.id,
        }).then((response) => {
          if (response === 'deleted')
            setPreloadedRecipients((pr) => pr && pr.filter((r) => !r.group || r.groupID !== editGroupState.group!.id))
          else
            setPreloadedRecipients(
              (pr) => pr && pr.map((r) => (r.group && r.groupID === response.groupID ? response : r))
            )
          onSaved()
          setEditGroupState(undefined)
        })
      : makeRequest<StudentGroupRecipient>('/frontend_api/makeGroup', {
          name,
          studentIDs: students.map((student) => student.id),
          teacherIDs: teachers.map((teacher) => teacher.id),
        }).then((response) => {
          setPreloadedRecipients((pr) => pr && [...pr, response])
          onSaved()
          setEditGroupState(undefined)
        })
    req.catch(showAxiosErrorAsToast).finally(() => setSaving(false))
  }

  const getBestHeaderIndex = (header: string[]) => {
    let bestHeaderIndex: number | undefined = undefined
    for (const headerTitle of [
      'student_id',
      'student id',
      'sync_id',
      'id',
      'email',
      'student email',
      'student_email',
    ]) {
      const matchIndex = header.findIndex(
        (h) =>
          h
            .toLowerCase()
            .replace(/(^"|"$)/g, '')
            .trim() === headerTitle
      )
      if (matchIndex !== -1) {
        bestHeaderIndex = matchIndex
        break
      }
    }
    return bestHeaderIndex
  }

  const handleStudentUpload = async () => {
    if (
      !(await Popups.confirm({
        title: <b>Press `OK` to upload students to this group.</b>,
        message: (
          <>
            The file should be a csv with a column <b>'student_id'</b> or <b>'email'</b>, that we will use to find
            students to add to the group.
          </>
        ),
        yesText: 'OK',
      }))
    )
      return

    const file = await inputSingleFile('text/csv')
    if (!file) {
      Toasts.showToast({
        message: 'Invalid file selected',
        error: true,
      })
      return
    }

    setUploading(true)
    try {
      const text = await file.text()
      const lines = text.split('\n')
      const header = lines[0].trim().split(',')
      const bestHeaderIndex = getBestHeaderIndex(header)

      let identifiers = []
      if (bestHeaderIndex !== undefined) {
        lines.splice(0, 1) // remove header
        identifiers = lines.map((l) => l.split(',')[bestHeaderIndex])
      } else {
        // assume there is no header, take all column 0
        identifiers = lines.map((l) => l.split(',')[0])
      }
      identifiers = identifiers
        .filter((i) => i !== undefined)
        .map((i) => i.replace(/(^"|"$)/g, '').trim())
        .filter((i) => i !== '')

      const newStudents = await loadBasicUsersByIdentifiers('student')(identifiers)

      if (newStudents.length === 0) {
        Toasts.showToast({
          message: 'No students found from the uploaded file',
          error: true,
        })
        return
      }

      // Merge new students with existing ones, avoiding duplicates
      setStudents((prevStudents) => {
        const existingIds = new Set(prevStudents.map((s) => s.id))
        const studentsToAdd = newStudents.filter((s) => !existingIds.has(s.id))

        if (studentsToAdd.length === 0) {
          Toasts.showToast({
            message: 'All students from the file are already in the group',
            error: false,
          })
          return prevStudents
        }

        Toasts.showToast({
          message: `Added ${studentsToAdd.length} new student${studentsToAdd.length > 1 ? 's' : ''} to the group`,
          error: false,
        })

        return [...prevStudents, ...studentsToAdd]
      })
    } catch (error) {
      Toasts.showToast({
        message: 'Error processing the file',
        error: true,
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <ControlledModal
      open={true}
      onOpenChange={() => {
        setEditGroupState(undefined)
      }}
      size="sm"
      disabled={saving}
      title={editGroupState.group ? 'Edit Group' : 'New Group'}
      className="gap-3"
    >
      <TextInput value={name} onChange={setName} placeholder="Group Name" />
      <div className="-mt-3 text-sm">Recipients will see this name when you send to this group.</div>
      {(!editGroupState.group || editGroupState.group?.isCustomGroup) && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <UserSelector<BasicUser>
              className="flex-grow"
              selectedUsers={students}
              setSelectedUsers={setStudents}
              fetchUsersByIdentifiersForPaste={loadBasicUsersByIdentifiers('student')}
              userDescriptor="students"
              url="/frontend_api/groupStudentRecipients"
              searchData={recipientSearchData}
            />
            {user.massCallingEnabled && (
              <IconFileUpload
                className={`cursor-pointer transition-opacity ${
                  uploading ? 'cursor-not-allowed opacity-25' : 'opacity-50 hover:opacity-75'
                }`}
                tooltip="Upload a CSV file of students"
                onClick={uploading ? undefined : handleStudentUpload}
              />
            )}
          </div>
          {uploading && <div className="text-sm text-gray-500">Uploading students...</div>}
        </div>
      )}
      {(!editGroupState.group || editGroupState.group?.isCustomGroup) && (
        <div className="flex items-center gap-2">
          <Button
            logo
            shape={ButtonShape.iconCircleSmall}
            className="pointer-events-none"
            tooltip="These teachers can also view and edit this group"
          >
            <IconShare />
          </Button>
          <UserSelector<BasicUser>
            className="flex-grow"
            selectedUsers={teachers}
            setSelectedUsers={setTeachers}
            fetchUsersByIdentifiersForPaste={loadBasicUsersByIdentifiers('teacher')}
            userDescriptor="teachers"
            url="/frontend_api/groupTeacherRecipients"
            searchData={recipientSearchData}
          />
        </div>
      )}
      <Button logo className="self-start" onClick={save} disabled={!canSave || uploading}>
        {uploading ? 'Uploading...' : 'Save'}
      </Button>
    </ControlledModal>
  )
}

export const deleteGroup = async (
  groupID: number,
  setPreloadedRecipients: React.Dispatch<React.SetStateAction<(TeacherRecipient | StudentRecipient)[] | undefined>>
) => {
  if (!(await Popups.simpleConfirm('Are you sure you want to delete this group?'))) return false
  try {
    await makeRequest('/frontend_api/deleteGroup', { id: groupID })
    setPreloadedRecipients((pr) => pr && pr.filter((r) => !r.group || r.groupID !== groupID))
    return true
  } catch (e) {
    showAxiosErrorAsToast(e)
    return false
  }
}
